import { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import ReactDOMServer from "react-dom/server";
import maplibregl from "maplibre-gl";

import Modal from "../common_modal/Modal";

import { set_value_properties } from "../../App/actions/propertiesActions";
import { set_value_sini } from "../../App/actions/sini_v2";
import { set_value_layer } from "../../App/actions/layerActions";
import { get_sini_data } from "../../App/actions/get_sini_data";

import dict from "../../Data/dict.json";
import uuid from "../../App/validation/uuid";
import domain_list from "../../Data/domain_list";

const pointer_id = "geojson_gps";
const geometry_pointer_id = `${pointer_id}_preview`;
const geometry_line_id = `${pointer_id}_preview_line`;

const LAYER_SEARCH = ({
  properties,
  layer,
  set_value_properties,
  auth,
  get_sini_data,
  set_value_sini,
  set_value_layer,
}) => {
  const [modal_sini_trigger, set_modal_sini_trigger] = useState(false);

  const toggle_sini_trigger = useCallback(() => {
    set_modal_sini_trigger((prev) => !prev);
  }, []);

  const on_remove_marker = useCallback(() => {
    const { map_object } = layer;

    if (map_object) {
      if (map_object.getLayer(pointer_id)) map_object.removeLayer(pointer_id);
      if (map_object.getLayer(geometry_pointer_id))
        map_object.removeLayer(geometry_pointer_id);
      if (map_object.getLayer(geometry_line_id))
        map_object.removeLayer(geometry_line_id);

      if (map_object.getSource(pointer_id)) map_object.removeSource(pointer_id);
      if (map_object.getSource(geometry_pointer_id))
        map_object.removeSource(geometry_pointer_id);
      if (map_object.getSource(geometry_line_id))
        map_object.removeSource(geometry_line_id);
    }

    const marker_element = document.getElementById("search_map");
    if (marker_element) {
      marker_element.remove();
      set_value_properties({
        key: "search_geometry",
        value: null,
      });
      set_value_properties({
        key: "uuid_search",
        value: "",
      });
    }
  }, [layer, set_value_properties]);

  const trigger_sini = useCallback(async () => {
    toggle_sini_trigger();
    set_value_properties({
      key: "sini_data_mode",
      value: "Site Analysis",
    });

    set_value_layer({
      key: "statistic_mode",
      value: "sini",
    });

    const { domain } = auth;
    const show_survey_1 = domain_list?.[domain]?.show_survey_1;
    const { search_geometry } = properties;
    const { type, coordinates, center } = search_geometry || {};
    let latitude = null;
    let longitude = null;

    if (type === "Point") {
      longitude = coordinates[0];
      latitude = coordinates[1];
    } else {
      longitude = +center.long;
      latitude = +center.lat;
    }

    const params = {
      request_id: uuid(),
      long: longitude,
      lat: latitude,
      km_rad: 1,
      sini_data_mode: "sini_general",
      show_survey_1,
    };

    on_remove_marker();

    await get_sini_data(params);

    set_value_sini({
      key: "sini_menu_active",
      value: "",
    });
    set_value_sini({
      key: "is_sini_visible",
      value: true,
    });
    set_value_layer({
      key: "statistic_mode",
      value: "sini",
    });
  }, [
    toggle_sini_trigger,
    set_value_properties,
    set_value_layer,
    auth,
    properties,
    on_remove_marker,
    get_sini_data,
    set_value_sini,
  ]);

  const on_render = useCallback(() => {
    const language = localStorage?.language || "ina";
    const { map_object } = layer;
    const { search_geometry } = properties;

    if (!search_geometry) return;

    const geometry = {
      type: search_geometry.type,
      coordinates: search_geometry.coordinates,
    };

    const { type, coordinates, center } = search_geometry || {};
    let latitude = null;
    let longitude = null;

    let layout = {};

    if (type === "Point") {
      longitude = coordinates[0];
      latitude = coordinates[1];

      if (map_object?.getSource(geometry_pointer_id)) {
        map_object.removeSource(geometry_pointer_id);
      }

      if (map_object?.getLayer(geometry_pointer_id)) {
        map_object.removeLayer(geometry_pointer_id);
      }

      if (map_object?.getSource(geometry_line_id)) {
        map_object.removeSource(geometry_line_id);
      }

      if (map_object?.getLayer(geometry_line_id)) {
        map_object.removeLayer(geometry_line_id);
      }
    } else {
      longitude = center.long;
      latitude = center.lat;

      let geojson = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {},
            geometry,
          },
        ],
      };

      let paint_type = "line";
      let paint = {
        "line-color": "#21a5f6",
        "line-width": 4,
      };

      layout = {
        "line-cap": "round",
        "line-join": "round",
      };

      if (type === "Polygon") {
        paint_type = "fill";
        paint = {
          "fill-color": "#a0d061",
          "fill-opacity": 0.2,
          "fill-outline-color": "#000000",
        };
      }

      let body = {
        id: geometry_pointer_id,
        source: geometry_pointer_id,
        type: paint_type,
        paint,
      };
      if (!map_object?.getSource(geometry_pointer_id)) {
        map_object.addSource(geometry_pointer_id, {
          type: "geojson",
          data: geojson,
        });
      } else {
        map_object.getSource(geometry_pointer_id).setData(geojson);
      }

      const layer_existing = map_object?.getLayer(geometry_pointer_id);
      if (!layer_existing) {
        map_object.addLayer(body);
      } else {
        if (layer_existing.type !== paint_type) {
          map_object.removeLayer(geometry_pointer_id);
          map_object.addLayer(body);
        }
      }

      if (!map_object?.getSource(geometry_line_id)) {
        map_object.addSource(geometry_line_id, {
          type: "geojson",
          data: geojson,
        });
      } else {
        map_object.getSource(geometry_line_id).setData(geojson);
      }

      if (!map_object?.getLayer(geometry_line_id)) {
        map_object.addLayer({
          id: geometry_line_id,
          source: geometry_line_id,
          type: "line",
          paint: {
            "line-color": "#21a5f6",
            "line-width": 4,
          },
          layout: layout,
        });
      }
    }

    if (map_object && longitude !== null && latitude !== null && type) {
      let marker_element = document.getElementById("search_map");
      if (!marker_element) {
        marker_element = document.createElement("div");
      }
      marker_element.id = "search_map";
      marker_element.style.position = "absolute";
      marker_element.style.transform = "translate(-50%, -100%)";
      const marker_jsx = (
        <section
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <button
            className="badge button_white outline_black"
            id="gps_title"
            aria-label="GPS title untuk menampilkan status"
          >
            {dict["Result"][language]}
          </button>
          <div
            style={{ width: "2px", height: "25px", backgroundColor: "black" }}
          />
        </section>
      );
      const marker_html = ReactDOMServer.renderToString(marker_jsx);
      marker_element.innerHTML = marker_html;
      const lngLat = new maplibregl.LngLat(longitude, latitude);
      const point = map_object.project(lngLat);
      marker_element.style.left = `${point.x}px`;
      marker_element.style.top = `${point.y}px`;
      map_object.getContainer().appendChild(marker_element);
      const gps_title = document.getElementById("gps_title");
      if (gps_title) {
        gps_title.addEventListener("click", toggle_sini_trigger);
      }
      map_object.on("move", () => {
        const newPoint = map_object.project(lngLat);
        marker_element.style.left = `${newPoint.x}px`;
        marker_element.style.top = `${newPoint.y}px`;
      });
      const geojson_gps = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [longitude, latitude],
            },
            properties: {},
          },
        ],
      };
      if (!map_object.getSource("geojson_gps")) {
        map_object.addSource("geojson_gps", {
          type: "geojson",
          data: geojson_gps,
        });
      } else {
        map_object.getSource("geojson_gps").setData(geojson_gps);
      }
      if (!map_object.getLayer("geojson_gps")) {
        map_object.addLayer({
          id: "geojson_gps",
          source: "geojson_gps",
          type: "circle",
          paint: {
            "circle-radius": 5,
            "circle-color": "#000",
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          },
          layout: { visibility: "visible", ...layout },
        });
      }
    }
  }, [layer, properties, toggle_sini_trigger]);

  const on_fly = useCallback(() => {
    const { map_object, gps_object } = layer;
    const longitude = gps_object?.longitude;
    const latitude = gps_object?.latitude;
    if (map_object && longitude && latitude) {
      map_object.flyTo({
        center: [longitude, latitude],
        speed: 1,
        zoom: 18,
      });
    }
  }, [layer]);

  useEffect(() => {
    const { map_object } = layer;
    const { uuid_search } = properties;

    // if (map_object !== null) {
    //   if (!map_object.isStyleLoaded()) {
    //     map_object.once("styledata", () => {
    //       on_render();
    //       if (uuid_search) {
    //         on_fly();
    //       }
    //     });
    //   } else {
    //     on_render();
    //     if (uuid_search) {
    //       on_fly();
    //     }
    //   }
    // }

    if (map_object) {
      if (uuid_search) {
        on_render();
        on_fly();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.uuid_search, properties.basemap_used]);

  const language = localStorage?.language || auth.language;
  const { isAuthenticated } = auth;

  const modal_sini_trigger_content = modal_sini_trigger && (
    <Modal
      modalSize="small"
      isOpen={modal_sini_trigger}
      onClose={toggle_sini_trigger}
    >
      <div className="box-body" id="box-body">
        <section className="text_center">
          {isAuthenticated && (
            <>
              <button
                onClick={trigger_sini}
                className="button_big button_white margin_bottom outline_black"
              >
                {dict?.["Try SINI at your location"]?.[language]}
              </button>
              <br />
              <br />
            </>
          )}
          <button
            className="button_big background_red margin_bottom"
            onClick={on_remove_marker}
          >
            {dict?.["Close"]?.[language]}
          </button>
        </section>
      </div>
    </Modal>
  );

  return <main>{modal_sini_trigger_content}</main>;
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  layer: state.layer,
  properties: state.properties,
});

export default connect(mapStateToProps, {
  get_sini_data,
  set_value_sini,
  set_value_layer,
  set_value_properties,
})(LAYER_SEARCH);
